import React, { useState, useEffect, useMemo } from 'react';
import Icon from './ui-stubs';
import { Button } from './ui-stubs';
import { Select } from './ui-stubs';

const ViewToolbar = ({ 
  currentView, 
  onViewChange, 
  sortBy, 
  onSortChange, 
  savedViews, 
  onSaveView, 
  onLoadView, 
  onSetDefaultView, 
  onDeleteView,
  density,
  onDensityChange,
  filters
}) => {
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [showManageViews, setShowManageViews] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [isShared, setIsShared] = useState(false);
  
  // Per-view state memory (UI only)
  const [viewPreferences, setViewPreferences] = useState({
    list: { sortBy: 'priority', sortDirection: 'asc', density: 'comfortable' },
    kanban: { sortBy: 'priority', sortDirection: 'asc', density: 'comfortable' },
    calendar: { sortBy: 'dueDate', sortDirection: 'asc', density: 'comfortable' },
    timeline: { sortBy: 'startDate', sortDirection: 'asc', density: 'comfortable' }
  });
  
  const [sortDirection, setSortDirection] = useState('asc');

  const views = [
    { id: 'list', label: 'List', icon: 'List' },
    { id: 'kanban', label: 'Kanban', icon: 'Columns' },
    { id: 'calendar', label: 'Calendar', icon: 'Calendar' },
    { id: 'timeline', label: 'Timeline', icon: 'GanttChart' },
  ];

  // View-aware sort options
  const getSortOptionsForView = (view) => {
    switch (view) {
      case 'list': case'kanban':
        return [
          { value: 'priority', label: 'Priority' },
          { value: 'dueDate', label: 'Due Date' },
          { value: 'lastUpdated', label: 'Last Updated' },
        ];
      case 'calendar':
        return [
          { value: 'dueDate', label: 'Due Date' },
        ];
      case 'timeline':
        return [
          { value: 'startDate', label: 'Start Date' },
          { value: 'endDate', label: 'End Date' },
        ];
      default:
        return [
          { value: 'priority', label: 'Priority' },
          { value: 'dueDate', label: 'Due Date' },
          { value: 'lastUpdated', label: 'Last Updated' },
        ];
    }
  };

  const sortOptions = getSortOptionsForView(currentView);

  // Restore per-view preferences when switching views
  useEffect(() => {
    const prefs = viewPreferences?.[currentView];
    if (prefs) {
      onSortChange(prefs?.sortBy);
      setSortDirection(prefs?.sortDirection);
      onDensityChange(prefs?.density);
    }
  }, [currentView]);

  // Save preferences when they change
  const updateViewPreference = (key, value) => {
    setViewPreferences(prev => ({
      ...prev,
      [currentView]: {
        ...prev?.[currentView],
        [key]: value
      }
    }));
  };

  const handleSortChange = (newSortBy) => {
    onSortChange(newSortBy);
    updateViewPreference('sortBy', newSortBy);
  };

  const handleSortDirectionToggle = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);
    updateViewPreference('sortDirection', newDirection);
  };

  const handleDensityChange = (newDensity) => {
    onDensityChange(newDensity);
    updateViewPreference('density', newDensity);
  };

  const handleViewChange = (viewId) => {
    onViewChange(viewId);
  };

  const handleSaveView = () => {
    if (newViewName?.trim()) {
      onSaveView({
        name: newViewName,
        isShared: isShared,
        sortBy,
        sortDirection,
        density,
        filters
      });
      setNewViewName('');
      setIsShared(false);
      setShowSaveViewModal(false);
    }
  };

  const handleLoadView = (viewId) => {
    onLoadView(viewId);
    setShowManageViews(false);
  };

  const handleSetDefaultView = (viewId) => {
    onSetDefaultView(viewId);
  };

  // Check if current state has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    // Check if any filters are applied
    const hasActiveFilters = 
      filters?.status?.length > 0 ||
      filters?.ownerIds?.length > 0 ||
      filters?.contributorIds?.length > 0 ||
      filters?.checklistAssigneeIds?.length > 0 ||
      filters?.priorities?.length > 0 ||
      filters?.tags?.length > 0 ||
      filters?.taskType ||
      filters?.hasDependencies ||
      filters?.myWork;

    // Check if sort differs from default
    const defaultSort = viewPreferences?.[currentView]?.sortBy || 'priority';
    const sortDiffers = sortBy !== defaultSort;

    // Check if density is not comfortable
    const densityDiffers = density !== 'comfortable';

    return hasActiveFilters || sortDiffers || densityDiffers;
  }, [filters, sortBy, density, currentView, viewPreferences]);

  // Check if current state matches an existing saved view
  const matchesExistingView = useMemo(() => {
    return savedViews?.some(view => {
      const filtersMatch = JSON.stringify(view?.filters) === JSON.stringify(filters);
      const sortMatch = view?.sortBy === sortBy;
      const viewTypeMatch = view?.viewType === currentView;
      return filtersMatch && sortMatch && viewTypeMatch;
    });
  }, [savedViews, filters, sortBy, currentView]);

  const shouldDisableSave = !hasUnsavedChanges || matchesExistingView;

  // Get default view
  const defaultView = savedViews?.find(v => v?.isDefault);

  return (
    <div className="bg-card border-b border-border px-6 py-3 sticky top-[72px] z-30">
      <div className="flex items-center justify-between">
        {/* Left: View Switcher */}
        <div className="flex items-center space-x-2">
          {views?.map((view) => {
            const isDefault = defaultView?.viewType === view?.id;
            return (
              <button
                key={view?.id}
                onClick={() => handleViewChange(view?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${
                  currentView === view?.id
                    ? 'bg-primary text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon name={view?.icon} size={16} />
                <span>{view?.label}</span>
                {isDefault && (
                  <Icon 
                    name="Star" 
                    size={12} 
                    className={currentView === view?.id ? 'text-yellow-300' : 'text-yellow-500'}
                    fill={currentView === view?.id ? '#fde047' : '#eab308'}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Right: Sort, Density & View Controls */}
        <div className="flex items-center space-x-3">
          {/* Sort Dropdown with Direction Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Sort by:</span>
            <Select
              value={sortBy}
              onChange={handleSortChange}
              options={sortOptions}
              className="w-40"
            />
            <button
              onClick={handleSortDirectionToggle}
              className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
              title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
            >
              <Icon 
                name={sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                size={16} 
                className="text-gray-700"
              />
            </button>
          </div>

          {/* Save View Button with Intelligence */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              iconName="Save"
              onClick={() => setShowSaveViewModal(true)}
              disabled={shouldDisableSave}
              className="relative"
            >
              Save View
              {hasUnsavedChanges && !matchesExistingView && (
                <span 
                  className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full"
                  title="This view has unsaved changes"
                />
              )}
            </Button>
            {shouldDisableSave && (
              <div className="absolute top-full mt-1 right-0 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                {matchesExistingView ? 'No changes to save' : 'Customize view to enable saving'}
              </div>
            )}
          </div>

          {/* Manage Views Dropdown */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              iconName="Settings"
              onClick={() => setShowManageViews(!showManageViews)}
            >
              Manage Views
            </Button>

            {showManageViews && (
              <div className="absolute right-0 mt-2 w-72 bg-card border border-border rounded-lg shadow-lg z-50">
                <div className="p-3 border-b border-border">
                  <h3 className="text-sm font-semibold text-gray-900">Saved Views</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {savedViews?.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      No saved views yet
                    </div>
                  ) : (
                    savedViews?.map((view) => (
                      <div
                        key={view?.id}
                        className="px-3 py-2 hover:bg-gray-50 flex items-center justify-between group"
                      >
                        <button
                          onClick={() => handleLoadView(view?.id)}
                          className="flex items-center space-x-2 flex-1 text-left"
                        >
                          {view?.isDefault && (
                            <Icon
                              name="Star"
                              size={14}
                              className="text-yellow-500"
                              fill="#eab308"
                            />
                          )}
                          <Icon
                            name={view?.isShared ? 'Users' : 'User'}
                            size={14}
                            className="text-gray-400"
                          />
                          <span className="text-sm text-gray-700">{view?.name}</span>
                          {view?.isDefault && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              Default
                            </span>
                          )}
                          {view?.isShared && (
                            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                              Shared
                            </span>
                          )}
                        </button>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleSetDefaultView(view?.id)}
                            className={`p-1 rounded transition-colors ${
                              view?.isDefault 
                                ? 'text-yellow-500 hover:text-yellow-600' :'text-gray-400 hover:text-primary'
                            }`}
                            title={view?.isDefault ? 'Default view' : 'Set as default'}
                          >
                            <Icon 
                              name="Star" 
                              size={14} 
                              fill={view?.isDefault ? '#eab308' : 'none'}
                            />
                          </button>
                          <button
                            onClick={() => onDeleteView(view?.id)}
                            className="text-gray-400 hover:text-red-600 p-1"
                            title="Delete view"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save View Modal */}
      {showSaveViewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Save Current View</h2>
              <button
                onClick={() => setShowSaveViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  View Name
                </label>
                <input
                  type="text"
                  value={newViewName}
                  onChange={(e) => setNewViewName(e?.target?.value)}
                  placeholder="e.g., My Sprint Tasks"
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="shared-view"
                  checked={isShared}
                  onChange={(e) => setIsShared(e?.target?.checked)}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="shared-view" className="text-sm text-gray-700">
                  Share with team
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  This view will save your current filters, sort order, sort direction, density, and view type.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowSaveViewModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveView}
                disabled={!newViewName?.trim()}
              >
                Save View
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewToolbar;